import { OpenInNewRounded } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { fetchSubscriptionInvoices } from "@/api/invoiceSync";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import BlotterNote from "@/components/BlotterNote";
import StatusBadge, { type StatusTone } from "@/components/StatusBadge";
import { TruncatedText } from "@/components/ui";
import { useSubscriptionInvoices } from "@/hooks/useInvoiceSync";
import type { SubscriptionInvoiceRow, SubscriptionInvoicesQuery } from "@/interfaces/invoiceSync";
import { formatDate, formatDateTime } from "@/utils/date-format";

type Status = NonNullable<SubscriptionInvoicesQuery["status"]>;
type SortBy = NonNullable<SubscriptionInvoicesQuery["sortBy"]>;

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  open: "Open",
  paid: "Paid",
  uncollectible: "Uncollectible",
  void: "Void",
};
const STATUS_TONE: Record<string, StatusTone> = {
  draft: "neutral",
  open: "info",
  paid: "success",
  uncollectible: "error",
  void: "muted",
};
const STATUS_PILLS: { label: string; value: Status | null }[] = [
  { label: "All", value: null },
  { label: "Open", value: "open" },
  { label: "Paid", value: "paid" },
  { label: "Uncollectible", value: "uncollectible" },
  { label: "Void", value: "void" },
];
const SORTABLE = new Set<string>(["cycle", "organization", "amount", "status"]);

const money = (v: string | null, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/** Paid at Stripe with no payment booked in v2 is the one state the sync exists to fix. */
const paymentMissing = (r: SubscriptionInvoiceRow) =>
  r.status === "paid" && Number(r.amountPaid ?? 0) > 0 && !r.paymentBooked;

function StatusPills({
  active,
  onChange,
}: {
  active: Status | null;
  onChange: (v: Status | null) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {STATUS_PILLS.map((p) => {
        const isActive = active === p.value;
        return (
          <Button
            key={p.label}
            size="small"
            onClick={() => onChange(p.value)}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              border: "1px solid",
              borderColor: isActive ? "var(--color-rodeo-black)" : "var(--color-smoke)",
              bgcolor: isActive ? "var(--color-rodeo-black)" : "transparent",
              color: isActive ? "var(--color-white)" : "var(--color-graphite)",
              "&:hover": {
                bgcolor: isActive ? "var(--color-rodeo-black)" : "var(--color-cloud, #f2f0ec)",
              },
            }}
          >
            {p.label}
          </Button>
        );
      })}
    </Box>
  );
}

/**
 * Subscription invoices on Finance › Invoices: one row per billing cycle v2 holds, across all
 * organizations. GFG finance only (the route is FINANCE/view, scope all). Read-only — a cycle is
 * created by the Stripe webhook or by Sync with Stripe, never by hand.
 */
export default function SubscriptionInvoicesPanel() {
  const [status, setStatus] = useState<Status | null>(null);

  const queryOf = (q: {
    search: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page: number;
    limit: number;
  }): SubscriptionInvoicesQuery => ({
    status: status ?? undefined,
    search: q.search || undefined,
    sortBy: q.sortBy && SORTABLE.has(q.sortBy) ? (q.sortBy as SortBy) : undefined,
    sortDir: q.sortDir,
    page: q.page,
    limit: q.limit,
  });

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<SubscriptionInvoiceRow> => {
    const { data, isLoading } = useSubscriptionInvoices(queryOf(q));
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<SubscriptionInvoiceRow> = {
    title: "Subscription invoices",
    searchPlaceholder: "Search invoice number or organization",
    itemNoun: "invoice",
    rowKey: (r) => r.renewalId,
    defaultSortKey: "cycle",
    defaultSortDir: "desc",
    filterFields: [],
    useData,
    exportFilename: "subscription-invoices.csv",
    exportFetch: async (q) => {
      const res = await fetchSubscriptionInvoices(queryOf(q));
      return { data: res.data, totalPages: res.totalPages };
    },
    leadingCell: (r) =>
      r.hostedInvoiceUrl ? (
        <Tooltip title="Open in Stripe">
          <IconButton
            size="small"
            href={r.hostedInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Open in Stripe"
          >
            <OpenInNewRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
    columns: [
      {
        key: "number",
        label: "Invoice",
        render: (r) => (
          <Box sx={{ fontWeight: 600 }}>{r.stripeInvoiceNumber ?? r.stripeInvoiceId ?? "—"}</Box>
        ),
        exportValue: (r) => r.stripeInvoiceNumber ?? r.stripeInvoiceId ?? "",
      },
      {
        key: "organization",
        label: "Organization",
        sortable: true,
        sortValue: (r) => r.organizationName,
        render: (r) => <TruncatedText text={r.organizationName} />,
      },
      {
        key: "cycle",
        label: "Billing cycle",
        sortable: true,
        sortValue: (r) => r.cycleStart,
        render: (r) => `${formatDate(r.cycleStart)} – ${formatDate(r.cycleEnd)}`,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => (
          <StatusBadge
            label={STATUS_LABEL[r.status] ?? r.status}
            tone={STATUS_TONE[r.status] ?? "neutral"}
          />
        ),
        exportValue: (r) => STATUS_LABEL[r.status] ?? r.status,
      },
      {
        key: "amount",
        label: "Amount",
        sortable: true,
        sortValue: (r) => Number(r.amountDue ?? 0),
        render: (r) => money(r.amountDue, r.currency),
        exportValue: (r) => money(r.amountDue, r.currency),
      },
      {
        key: "paid",
        label: "Paid",
        render: (r) => formatDateTime(r.paidAt),
      },
      {
        key: "booked",
        label: "Payment booked?",
        render: (r) =>
          paymentMissing(r) ? (
            <Tooltip title="Paid at Stripe but not booked in v2. Sync with Stripe books it." arrow>
              <span>
                <StatusBadge label="Not booked" tone="warning" />
              </span>
            </Tooltip>
          ) : r.paymentBooked ? (
            "Yes"
          ) : (
            <Box component="span" sx={{ color: "var(--color-graphite)", opacity: 0.5 }}>
              —
            </Box>
          ),
        exportValue: (r) => (paymentMissing(r) ? "Not booked" : r.paymentBooked ? "Yes" : ""),
      },
    ],
  };

  return (
    <Box>
      <BlotterNote>
        <strong>Every subscription invoice v2 holds, across all organizations.</strong> Stripe
        raises these on each billing cycle; v2 books them from Stripe's webhook, and the daily sync
        catches any the webhook missed. <strong>Not booked</strong> means Stripe took the payment
        but v2 has not recorded it yet — Sync with Stripe fixes it.
      </BlotterNote>
      <StatusPills active={status} onChange={setStatus} />
      <Blotter config={config} />
    </Box>
  );
}
